import { i18n } from './i18n.js';
import { getSignInPresenter } from './app-services.js';
/**
 * MoonfallGuest — client orchestration for the play-first guest tutorial.
 *
 * Anonymous visitors play the tutorial BEFORE signing up. This module:
 *   - obtains a server-issued guest credential,
 *   - tracks whether a guest session is currently in play (so socket payloads
 *     attach the guest token instead of a Firebase token),
 *   - remembers, in localStorage, that a visitor already saw/skipped the tutorial
 *     so returning anonymous visitors are NOT re-trapped, and
 *   - drives the end-of-tutorial "sign-in moment" (delegated to MoonfallSignIn).
 *
 * It holds NO credential beyond the opaque server-signed token, and never blocks
 * the one-click Skip path.
 */
import { StoryFeed } from './ui/story-feed.js';
import { Tutorial } from './ui/tutorial.js';
import { GameState } from './game-state.js';
import { SocketHandler } from './socket-handler.js';
import { AssetPreloader } from './ui/asset-preloader.js';

const MoonfallGuest = (function () {
  'use strict';

  const SKIP_KEY = 'moonfall_guest_tutorial_skipped';
  const RESUME_KEY = 'moonfall_guest_tutorial_resume';
  const RELOAD_RESUME_KEY = 'moonfall_guest_tutorial_reload_resume';
  const MAX_START_ATTEMPTS = 3;

  let active = false;      // a guest tutorial is currently being played
  let token = null;        // current signed guest token
  let expiresAt = 0;
  let ended = false;       // guards double-firing of the end-of-tutorial moment
  let endedRoomId = null;  // room that ended, so later signed-in rooms are allowed
  let starting = false;
  let startPromise = null;
  let startGeneration = 0;
  const pendingStartWaits = new Set();

  function apiBase() {
    return (window.__MOONFALL_CONFIG && window.__MOONFALL_CONFIG.socketUrl) || '';
  }

  function isActive() {
    return active === true && typeof token === 'string' && token.length > 0;
  }

  // True once the guest has left the tutorial (skip/complete/limit) and has NOT
  // started a new guest run. Room-scoped checks ignore only stale events from the
  // completed guest room while allowing a later signed-in room to start normally.
  function isEnded(roomId = null) {
    if (ended !== true) return false;
    if (!roomId) return true;
    return !endedRoomId || roomId === endedRoomId;
  }

  function getToken() {
    return token;
  }

  function isStarting() {
    return starting === true;
  }

  function clearReloadResume() {
    try { window.sessionStorage.removeItem(RELOAD_RESUME_KEY); } catch (_) { /* no-op */ }
  }

  function readReloadResume() {
    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(RELOAD_RESUME_KEY) || 'null');
      if (!parsed || typeof parsed !== 'object'
        || typeof parsed.roomId !== 'string'
        || typeof parsed.playerId !== 'string'
        || typeof parsed.token !== 'string'
        || !Number.isFinite(Number(parsed.expiresAt))) {
        clearReloadResume();
        return null;
      }
      if (Date.now() >= Number(parsed.expiresAt)) {
        clearReloadResume();
        return null;
      }
      return {
        roomId: parsed.roomId,
        playerId: parsed.playerId,
        token: parsed.token,
        expiresAt: Number(parsed.expiresAt),
      };
    } catch (_) {
      clearReloadResume();
      return null;
    }
  }

  function rememberTutorial({ roomId, playerId } = {}) {
    if (!isActive() || typeof roomId !== 'string' || typeof playerId !== 'string') return false;
    try {
      window.sessionStorage.setItem(RELOAD_RESUME_KEY, JSON.stringify({
        roomId,
        playerId,
        token,
        expiresAt,
      }));
      return true;
    } catch (_) {
      return false;
    }
  }

  function clearSession(options = {}) {
    active = false;
    token = null;
    expiresAt = 0;
    if (options.clearReloadResume !== false) clearReloadResume();
    if (options && options.clearEnded === true) {
      ended = false;
      endedRoomId = null;
    }
  }

  function supersedeStart() {
    startGeneration += 1;
    for (const cancelWait of [...pendingStartWaits]) cancelWait();
    starting = false;
    startPromise = null;
  }

  /**
   * DEACTIVATING IS LEAVING, so it must disown the room too.
   *
   * Superseding the start generation cancels everything this module still
   * controls — every `ownsStart()` check after the socket emit bails. It cannot
   * cancel the SERVER. `startTutorial` is already on the wire, and
   * `game/socketHandlers.js` emits `gameStarted` BEFORE it replies, so the room
   * id is not knowable here: the board packet arrives first, and the ack that
   * would name the room arrives after. A room-scoped disown would therefore
   * disown nothing at all, which is why this one is unscoped.
   *
   * Without it the packet installs over an authenticated lobby. Reported from
   * an iPhone and reproduced at 844x280 in WebKit: Firebase reports signed-out
   * at boot, the guest tutorial auto-starts, Firebase then restores the real
   * user, the lobby paints with their own username — and ~250ms later the guest
   * board takes the screen. `isActive()` and `isStarting()` were both already
   * false at that moment; `isEnded()` was false, and `isEnded()` is the only
   * question the `gameStarted` guard asks.
   *
   * `endGuestTutorial` latches first and scopes to the room it was playing, so
   * a later signed-in room is still allowed; this never overwrites that. And an
   * authenticated start clears the latch through the `clearEnded` option —
   * socket-handler.js does it at every start and resume — so arming it here
   * cannot keep a signed-in player out of their own game.
   */
  function deactivate(options = {}) {
    supersedeStart();
    if (options.clearEnded !== true && ended !== true) {
      ended = true;
      endedRoomId = getCurrentRoomId();
    }
    clearSession(options);
  }

  function ownsStart(generation) {
    return generation === startGeneration;
  }

  // --- Returning-visitor memory (never a hard block; just avoids re-trapping) ---
  function hasSkipped() {
    try { return window.localStorage.getItem(SKIP_KEY) === 'true'; } catch (_) { return false; }
  }
  function markSkipped() {
    try { window.localStorage.setItem(SKIP_KEY, 'true'); } catch (_) { /* private mode: fine */ }
  }
  function clearSkip() {
    try { window.localStorage.removeItem(SKIP_KEY); } catch (_) { /* no-op */ }
  }

  function isResumeToken(value) {
    return typeof value === 'string' && value.length <= 4096 && value.split('.').length === 2;
  }

  function storeResumeToken(resumeToken) {
    if (!isResumeToken(resumeToken)) return false;
    try {
      window.sessionStorage.setItem(RESUME_KEY, JSON.stringify({
        resumeToken,
        savedAt: Date.now(),
      }));
    } catch (_) { /* private mode: fine */ }
    return true;
  }

  function clearResumeToken() {
    try { window.sessionStorage.removeItem(RESUME_KEY); } catch (_) { /* no-op */ }
  }

  function getResumeToken() {
    try {
      const raw = window.sessionStorage.getItem(RESUME_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return isResumeToken(parsed?.resumeToken) ? parsed.resumeToken : null;
    } catch (_) {
      return null;
    }
  }

  function consumeResumeToken() {
    const resumeToken = getResumeToken();
    clearResumeToken();
    return resumeToken;
  }

  function getCurrentRoomId() {
    try {
      const gameStateApi = (typeof GameState !== 'undefined' && GameState?.get)
        ? GameState
        : GameState;
      return gameStateApi?.get?.()?.roomId || null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Fetch a fresh guest token. Resolves { ok:true } or { ok:false, code, message }.
   * Never fakes success — a failed mint surfaces to the caller so it can fall back
   * to the sign-in screen with an honest message.
   */
  async function fetchToken(generation = startGeneration) {
    let res;
    try {
      res = await fetch(`${apiBase()}/api/guest/tutorial-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch (err) {
      return { ok: false, code: 'network', message: 'Could not reach the server.' };
    }
    if (res.status === 429) {
      let body = {};
      try { body = await res.json(); } catch (_) {}
      return {
        ok: false,
        code: body.code || 'rate_limited',
        message: body.error || 'Guest limit reached.',
        // The server's own answer: asking again is what it refused.
        refused: true,
      };
    }
    if (!res.ok) {
      return { ok: false, code: 'unavailable', message: 'Guest tutorial is unavailable right now.' };
    }
    let body;
    try { body = await res.json(); } catch (_) { body = null; }
    if (!body || typeof body.token !== 'string') {
      return { ok: false, code: 'malformed', message: 'Guest tutorial is unavailable right now.' };
    }
    if (!ownsStart(generation)) return { ok: false, code: 'superseded' };
    token = body.token;
    expiresAt = Number(body.expiresAt) || 0;
    active = true;
    warmFirstScreen(body.firstScreenArt);
    return { ok: true };
  }

  /**
   * THE FIRST SCREEN DOWNLOADS WHILE THE ROOM IS BUILT. The session names the
   * tutorial's opening pictures (both portraits and the player's own hand —
   * server: tutorialFirstScreenArt.js); they used to start only with the
   * room's first state, ~3.3 MB behind a reveal cap on a phone's first visit
   * (measured 25 September 2026). The match warm that follows joins these
   * same requests, so nothing is fetched twice.
   */
  function warmFirstScreen(urls) {
    if (!Array.isArray(urls)) return;
    const pictures = urls
      .filter((url) => typeof url === 'string' && url.length < 2048 && /^https:\/\/[^\s"'<>]+$/.test(url))
      .slice(0, 12);
    if (!pictures.length) return;
    try {
      void AssetPreloader?.warmSources?.(pictures, { priority: 'high' });
    } catch (_) { /* a warm is best effort; the match warm fetches them anyway */ }
  }

  /**
   * Entry point for an anonymous visitor's first play: mint a token and boot the
   * tutorial straight in. On any mint failure, fall back to the sign-in screen with
   * a clear reason (no silent degradation).
   */
  function tr(key, fallback, replacements) {
    try {
      const translated = i18n?.t?.(key, replacements || {});
      if (translated && translated !== key) return translated;
    } catch (_) { /* use fallback */ }
    return fallback;
  }

  function showWaiting(seconds) {
    getSignInPresenter()?.showFirebaseSignIn?.({
      returning: false,
      waitingForTutorial: true,
      notice: tr(
        'guest.preparingTutorial',
        `Preparing your tutorial… Starting automatically in ${seconds}s.`,
        { seconds },
      ),
    });
  }

  function waitForRetry(retryAfterMs, generation) {
    const safeMs = Math.max(250, Number(retryAfterMs) || 5000);
    const deadline = Date.now() + safeMs;
    return new Promise((resolve) => {
      let settled = false;
      let interval = null;
      let timeout = null;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (interval) clearInterval(interval);
        if (timeout) clearTimeout(timeout);
        pendingStartWaits.delete(finish);
        resolve();
      };
      pendingStartWaits.add(finish);
      if (!ownsStart(generation)) {
        finish();
        return;
      }
      showWaiting(Math.max(1, Math.ceil(safeMs / 1000)));
      interval = setInterval(() => {
        if (!ownsStart(generation)) {
          finish();
          return;
        }
        const remaining = Math.max(0, deadline - Date.now());
        if (remaining > 0) showWaiting(Math.ceil(remaining / 1000));
      }, 1000);
      timeout = setTimeout(finish, safeMs);
    });
  }

  /**
   * A FAILED START IS NOT A DEAD END. The card is told whether trying again
   * can help, and offers it when it can (main.js, `#auth-guest-retry`, which
   * calls `startOrResumeGuestTutorial`).
   *
   * It can when nothing was started and the server did not say no: the
   * connection failed, or the guest pass could not be fetched. Found on
   * production, 13 October 2026: 3 of 17 first visits ended on this card
   * reading "Failed to connect." with nothing to press except Google, while a
   * reload connected at once.
   *
   * It is NOT offered when the server refused (the daily cap, the start
   * throttle: the limit keeps its meaning) or when a start was sent and never
   * acknowledged, where a second start could become a second match.
   */
  function showStartFailure(message, { retry = false } = {}) {
    getSignInPresenter()?.showFirebaseSignIn?.({
      returning: hasSkipped(),
      notice: message || tr(
        'guest.startUnavailable',
        'The tutorial is unavailable right now. Please sign in or try again.',
      ),
      retry: retry === true,
    });
  }

  async function startFreshTutorial(generation) {
    if (!ownsStart(generation)) return false;
    // A new guest tutorial is starting — clear the end-of-tutorial latch so a
    // replayed run in the same page session can present the sign-in moment again.
    ended = false;
    endedRoomId = null;
    const result = await fetchToken(generation);
    if (!ownsStart(generation)) return false;
    if (!result.ok) {
      deactivate();
      // Honest fallback to the sign-in screen with the server's reason (e.g. daily
      // cap reached) — never a silent "pretend it worked".
      showStartFailure(result.message, { retry: result.refused !== true });
      return false;
    }
    if (!SocketHandler || typeof SocketHandler.startGuestTutorial !== 'function') {
      deactivate();
      showStartFailure();
      return false;
    }
    holdVeilForConnection();
    for (let attempt = 0; attempt < MAX_START_ATTEMPTS; attempt += 1) {
      const guestToken = token;
      const response = await SocketHandler.startGuestTutorial({
        guestToken,
        isCurrent: () => ownsStart(generation),
      });
      if (!ownsStart(generation)) return false;
      if (response?.ok) {
        rememberTutorial({ roomId: response.roomId, playerId: response.playerId });
        return true;
      }
      if (response?.code === 'too_many_attempts' && attempt < MAX_START_ATTEMPTS - 1) {
        await waitForRetry(response.retryAfterMs, generation);
        if (!ownsStart(generation)) return false;
        continue;
      }
      deactivate();
      if (serverIsBeingReplaced(response)) {
        showStartFailure(tr(
          'connection.serverUpdating',
          'Moonfall is updating. Your next battle can start in a minute.',
        ), { retry: true });
        return false;
      }
      showStartFailure(response?.message, { retry: neverReachedServer(response) });
      return false;
    }
    return false;
  }

  /* The start now waits on the connection, which may take its whole 10 s from
     here: the boot veil is held that long from now (main.js), whatever boot and
     the pass took before. */
  function holdVeilForConnection() {
    try {
      getSignInPresenter()?.holdVeilForGuestConnection?.();
    } catch (_) { /* the veil is presentation; the start goes on */ }
  }

  /* socket-handler.js answers `connection_failed` only for a request that was
     never put on the wire: nothing was started, so asking again is safe. A
     request that may have left answers `emit_failed`, which is not. */
  function neverReachedServer(response) {
    return response?.code === 'connection_failed';
  }

  /* A release is replacing the server (docs/release-process.md, "Step 1"):
     the start was refused before anything was built, so asking again is safe,
     and it will be taken once the new version answers. */
  function serverIsBeingReplaced(response) {
    return response?.code === 'server_updating' || response?.code === 'server_restarting';
  }

  /* Asking for a seat again cannot build a second match: the server re-seats
     the visitor or says the seat is gone. So a resume is asked for again
     whether or not the first request may have left. */
  function seatWasNotAnswered(response) {
    return neverReachedServer(response) || response?.code === 'emit_failed';
  }

  async function runStart({ resumeFirst }) {
    if (startPromise) return startPromise;
    const generation = startGeneration + 1;
    startGeneration = generation;
    starting = true;
    ended = false;
    endedRoomId = null;
    const ownedPromise = (async () => {
      if (resumeFirst) {
        const resume = readReloadResume();
        if (resume && SocketHandler?.resumeGuestTutorial) {
          if (!ownsStart(generation)) return false;
          token = resume.token;
          expiresAt = resume.expiresAt;
          active = true;
          holdVeilForConnection();
          const response = await SocketHandler.resumeGuestTutorial({
            roomId: resume.roomId,
            playerId: resume.playerId,
            guestToken: resume.token,
            isCurrent: () => ownsStart(generation),
          });
          if (!ownsStart(generation)) return false;
          if (response?.ok) return true;
          if (seatWasNotAnswered(response)) {
            /* THE SEAT WAS NOT REFUSED: THE SERVER WAS NOT REACHED. Throwing
               it away and starting fresh in the same breath built a second
               tutorial, as soon as the connection answered, for a visitor
               whose first was still waiting for them. The seat stays, and
               the card's retry asks for it again. */
            deactivate({ clearReloadResume: false });
            showStartFailure(response.message, { retry: true });
            return false;
          }
          clearSession();
        }
      }
      return startFreshTutorial(generation);
    })();
    startPromise = ownedPromise;
    try {
      return await ownedPromise;
    } finally {
      if (ownsStart(generation) && startPromise === ownedPromise) {
        starting = false;
        startPromise = null;
      }
    }
  }

  function startGuestTutorial() {
    clearReloadResume();
    return runStart({ resumeFirst: false });
  }

  function startOrResumeGuestTutorial() {
    return runStart({ resumeFirst: true });
  }

  /**
   * Replay: an explicit user choice from the sign-in screen. Clears the skip memory
   * and boots a fresh guest tutorial.
   */
  async function replayTutorial() {
    clearSkip();
    clearResumeToken();
    clearReloadResume();
    return startGuestTutorial();
  }

  /**
   * End the guest tutorial and present the sign-in moment. Called when the tutorial
   * completes, the player taps Skip, or the server signals the per-session limit.
   * `reason` is one of 'complete' | 'skip' | 'limit'. Always one click away from
   * signing in — never a trap.
   */
  function endGuestTutorial(reason, options = {}) {
    if (ended) return;
    ended = true;
    endedRoomId = getCurrentRoomId();
    const wasActive = isActive();
    deactivate();
    markSkipped();
    if (reason === 'complete' && isResumeToken(options.resumeToken)) {
      storeResumeToken(options.resumeToken);
    } else {
      clearResumeToken();
    }
    clearReloadResume();
    // Tear down any live tutorial UI/game so the sign-in moment isn't drawn over a
    // half-lit board.
    try { Tutorial?.end?.(); } catch (_) {}
    try { StoryFeed?.reset?.(); } catch (_) {}
    const signIn = getSignInPresenter();
    if (signIn && typeof signIn.showGuestSignInMoment === 'function') {
      signIn.showGuestSignInMoment({ reason, wasActive });
    }
  }

  /**
   * A SIGNED-IN PLAYER ARRIVED. The guest run is not theirs — abandon it.
   *
   * `deactivate()` alone disowns the room, which is enough while the board is
   * still on the wire. It is not enough once the board has installed: if
   * Firebase takes longer to restore the session than the server takes to build
   * the guest room, the player is already looking at a tutorial when their own
   * username appears in the top bar. Measured at 844x280 in WebKit with an
   * 800ms auth delay — board at t+553ms, username at t+1568ms.
   *
   * Deliberately NOT `endGuestTutorial('skip')`, which would do two wrong
   * things: present the guest sign-in moment to somebody who is already signed
   * in, and write the skip memory, silently costing them the tutorial on a
   * later signed-out visit from this browser. They skipped nothing.
   *
   * Returns whether a live board was abandoned, so the caller — which owns the
   * lobby view and this module does not — knows to switch back to it.
   */
  function abandonForSignedInUser() {
    const hadLiveBoard = isActive() && !!getCurrentRoomId();
    const hadAnyGuestWork = isActive() || isStarting();
    deactivate();
    if (hadAnyGuestWork) {
      /* Loud on purpose. Reaching here means the SDK reported signed-out first
         and the restored user second, so a guest tutorial was requested for a
         player who has an account — the race that put a tutorial board over an
         authenticated lobby. It is now handled, but it still costs a wasted
         guest token, a server-side tutorial game, and one of that IP's daily
         guest allowances, and it is otherwise invisible. */
      console.warn('[Guest] A signed-in user arrived after the guest tutorial had already started;'
        + ' abandoning it.', { hadLiveBoard });
    }
    if (!hadLiveBoard) return false;
    try { Tutorial?.end?.(); } catch (_) { /* teardown is best-effort */ }
    try { StoryFeed?.reset?.(); } catch (_) { /* teardown is best-effort */ }
    return true;
  }

  function completeWithResumeToken(resumeToken) {
    if (!isResumeToken(resumeToken)) return false;
    endGuestTutorial('complete', { resumeToken });
    return true;
  }

  return {
    abandonForSignedInUser,
    isActive,
    isStarting,
    isEnded,
    getToken,
    deactivate,
    clearReloadResume,
    rememberTutorial,
    getResumeToken,
    consumeResumeToken,
    clearResumeToken,
    hasSkipped,
    markSkipped,
    clearSkip,
    fetchToken,
    startGuestTutorial,
    startOrResumeGuestTutorial,
    replayTutorial,
    endGuestTutorial,
    completeWithResumeToken,
  };
})();

export { MoonfallGuest };
